import Axios, { type AxiosResponse } from 'axios';
import { call, put, select, takeLatest, takeEvery } from 'redux-saga/effects';

import { restClient } from 'config/store/configureStore';
import { TranslationOrigin } from 'constants/translationConstants';
import { putAndWait } from 'modules/app/sagas';
import { isApiFailure } from 'modules/global/generic.guards';
import type { ApiError, PutAndWaitResult } from 'modules/global/generic.types';
import { getLocale } from 'modules/i18n/selectors/data';
import { save as persistState } from 'modules/localStorage/actions';
import { getDefaultHeaders } from 'utils/RequestUtils';

import {
  fetchTranslations as fetchTranslationsAction,
  fetchTranslationsSuccess,
  fetchTranslationsFailure,
  switchLocaleSuccess,
  switchLocaleFailure,
} from './actions';
import * as types from './actionTypes';
import type { FetchTranslationsRequestAction, SwitchLocaleRequestAction, TranslationsByLocale } from './types';

function* fetchTranslations({ payload, meta }: FetchTranslationsRequestAction) {
  const { request, origin } = payload;
  try {
    const client = origin === TranslationOrigin.BO ? Axios : restClient;
    const { data }: AxiosResponse<TranslationsByLocale> = yield call(client.request, {
      ...request,
      headers: { ...request.headers, ...getDefaultHeaders() },
    });
    yield put(fetchTranslationsSuccess(data, meta));
  } catch (error) {
    yield put(fetchTranslationsFailure(error as ApiError, meta));
  }
}

function* fetchTranslationsAfterLocaleSwitch({ payload: locale }: SwitchLocaleRequestAction) {
  const response: PutAndWaitResult<TranslationsByLocale> = yield putAndWait(
    fetchTranslationsAction({ languages: [locale] })
  );
  if (isApiFailure(response)) {
    yield put(switchLocaleFailure(response.payload));
    return;
  }
  yield put(switchLocaleSuccess(locale));
  yield put(persistState());
}

/**
 * Loading the app translations is nothing more than fetching the active locale: the
 * `fetchTranslations` worker performs the call and fills the `messages` slice, and
 * `modules/app/sagas` waits on `FETCH_TRANSLATIONS_SUCCESS`/`FAILURE` to carry on booting.
 */
function* fetchAppTranslations() {
  const locale: ReturnType<typeof getLocale> = yield select(getLocale);
  yield put(fetchTranslationsAction({ languages: [locale] }));
}

export default function* i18nSagas() {
  yield takeEvery(types.FETCH_TRANSLATIONS_REQUEST, fetchTranslations);
  yield takeLatest(types.LOCALE_SWITCH, fetchTranslationsAfterLocaleSwitch);
  yield takeLatest(types.FETCH_APP_TRANSLATIONS_REQUEST, fetchAppTranslations);
}
